/**
 * Email Utilities
 * Functions for building email templates and handling email operations
 */

import { sanitizeString } from "./validation";

/**
 * Build HTML email for signature invitation
 * @param {Object} params
 * @param {string} params.message - Custom message
 * @param {string} params.link - Signature link
 * @param {string} params.documentName - Document name (optional)
 * @returns {string} HTML email content
 */
export function buildInvitationEmail({ message, link, documentName = "documento" }) {
  const safeMessage = (message || "").replace(/\n/g, "<br/>");
  const safeDocName = sanitizeString(documentName);
  
  return `
    <div style="font-family:system-ui,Segoe UI,Roboto,Arial,sans-serif;font-size:15px;color:#111;max-width:600px;margin:0 auto;">
      <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
        <h1 style="color:#e9eefc;margin:0;font-size:24px;">CEJM - Firma Digital</h1>
      </div>
      
      <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
        <p style="margin-top:0;">Hola,</p>
        <p>${safeMessage}</p>
        
        <div style="background:#f5f5f5;padding:16px;border-radius:8px;margin:20px 0;">
          <p style="margin:0;font-weight:600;color:#333;">Documento: ${safeDocName}</p>
        </div>
        
        <p style="margin-top:20px;text-align:center;">
          <a href="${link}" target="_blank"
             style="display:inline-block;background:#4f6cf7;color:#fff;text-decoration:none;
                    padding:14px 28px;border-radius:8px;font-weight:600;font-size:16px;">
            Abrir y firmar documento
          </a>
        </p>
        
        <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
        
        <p style="color:#999;font-size:12px;margin-bottom:0;">
          Este mensaje fue enviado automáticamente por el sistema de firma digital de CEJM.
        </p>
      </div>
    </div>
  `;
}

/**
 * Build HTML email for signed document notification
 * @param {Object} params
 * @param {string} params.documentName - Document name
 * @param {string} params.signerName - Signer's name
 * @param {string} params.signerDni - Signer's DNI
 * @param {string} params.signedDate - Signed date string
 * @param {string} params.downloadUrl - Download URL
 * @returns {string} HTML email content
 */
export function buildSignedDocumentEmail({
  documentName,
  signerName,
  signerDni,
  signedDate,
  downloadUrl,
}) {
  const safeDocName = sanitizeString(documentName);
  const safeName = sanitizeString(signerName);
  const safeDni = sanitizeString(signerDni);
  const safeDate = sanitizeString(signedDate);
  
  return `
    <div style="font-family:system-ui,Segoe UI,Roboto,Arial,sans-serif;font-size:15px;color:#111;max-width:600px;margin:0 auto;">
      <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
        <h1 style="color:#e9eefc;margin:0;font-size:24px;">CEJM - Documento Firmado</h1>
      </div>
      
      <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
        <p style="margin-top:0;">El documento <strong>${safeDocName}</strong> ha sido firmado correctamente.</p>
        
        <div style="background:#f0f9ff;padding:16px;border-radius:8px;margin:20px 0;border-left:4px solid #4f6cf7;">
          <p style="margin:0 0 8px;"><strong>Firmado por:</strong> ${safeName}</p>
          <p style="margin:0 0 8px;"><strong>DNI:</strong> ${safeDni}</p>
          <p style="margin:0;"><strong>Fecha:</strong> ${safeDate}</p>
        </div>
        
        <p>El documento firmado está adjunto a este correo.</p>
        
        <p style="margin-top:20px;text-align:center;">
          <a href="${downloadUrl}" target="_blank"
             style="display:inline-block;background:#2d7a4f;color:#fff;text-decoration:none;
                    padding:14px 28px;border-radius:8px;font-weight:600;font-size:16px;">
            Descargar documento
          </a>
        </p>
        
        <p style="margin-top:20px;font-size:13px;color:#666;">
          También puedes descargarlo desde: <a href="${downloadUrl}" style="color:#4f6cf7;">${downloadUrl}</a>
        </p>
        
        <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
        
        <p style="color:#999;font-size:12px;margin-bottom:0;">
          Este correo fue enviado automáticamente por el sistema de firma digital de CEJM.
        </p>
      </div>
    </div>
  `;
}

/**
 * Replace template variables in message
 * @param {string} template - Template string with {variables}
 * @param {Object} variables - Object with variable values
 * @returns {string} Message with variables replaced
 */
export function replaceTemplateVariables(template, variables) {
  if (!template) return "";
  
  let result = template;
  
  Object.keys(variables).forEach((key) => {
    const placeholder = `{${key}}`;
    result = result.replace(new RegExp(placeholder, "g"), variables[key] || "");
  });
  
  return result;
}

/**
 * Normalize email list (string or array) to array
 * @param {string|string[]} emails - Email or array of emails
 * @returns {string[]} Array of emails
 */
export function normalizeEmailList(emails) {
  if (!emails) return [];
  if (Array.isArray(emails)) return emails.filter(Boolean);
  if (typeof emails === "string") return [emails];
  return [];
}
